import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import React from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import "../page-structure/css/popup.css";
import Graph from "../Graph";
import EventInfo from "../EventInfo";
import { getOpenedCurrencies, isMobile, processCommand, removeOpenedCurrency } from "../../utils/utils";
import TradePopup from "./TradePopup";
import ChartEvaluationPopup from "./ChartEvaluationPopup";
import { ageShort } from "../../history/historyUtils";
import { FaInfoCircle, FaHistory, FaStar, FaRegStar, FaMagic } from "react-icons/fa";
import TradingPanel from "../../trades/TradingPanel";
import AssetLabel from "../common/AssetLabel";
import { comboFeedLabel, comboHeaderTitle } from "../comboEventLabels";
import { buildChartEvaluationTradePrefill, selectExecutableEvaluationPlan } from "../../chartEvaluation/chartEvaluationTrade";
import CodexEvaluationReport from "../CodexEvaluationReport";
import { useUserSession } from "../../auth/UserSessionContext";

function CurrencyPopup(props) {
    const { status: userSessionStatus } = useUserSession();

    const [btnDisabled, setBtnDisabled] = useState(isMobile());
    const [tradeWindow, setTradeWindow] = useState(false);
    const [priceBy, setPriceBy] = useState(null);
    const [tradeData, setTradeData] = useState({});
    const [eventInfo, setEventInfo] = useState(null);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [selectedCurrency, setSelectedCurrency] = useState("");
    const [sectionTitleWhenFromHistory, setSectionTitleWhenFromHistory] = useState(null);
    const [sectionEventTFWhenFromHistory, setSectionEventTFWhenFromHistory] = useState(null);
    const [initCurrencyDate, setInitCurrencyDate] = useState(new Date().getTime());
    const [feedTab, setFeedTab] = useState("this_section"); // "all" | "this_section" for history feed
    const [feedSelectionByUser, setFeedSelectionByUser] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [starSelected, setStarSelected] = useState(false);
    const [tradingPanelOpen, setTradingPanelOpen] = useState(false);
    const [chartTimeframe, setChartTimeframe] = useState(null);
    const [selectedTradeCandle, setSelectedTradeCandle] = useState(null);
    const [showChartEvaluation, setShowChartEvaluation] = useState(false);
    const [selectedCodexEvaluation, setSelectedCodexEvaluation] = useState(null);
    const feedScrollRef = useRef(null);
    const prevSelectedCurrencyRef = useRef(null);

    useEffect(() => {
        const cur = props.selectedCurrency;
        if (prevSelectedCurrencyRef.current == null && cur != null) {
            setFeedSelectionByUser(false);
        }
        prevSelectedCurrencyRef.current = cur;
    }, [props.selectedCurrency]);

    const getCurrency = useCallback(() => {
        return props.selectedCurrency?.sectionEvents?.[props.selectedCurrency?.index];
    }, [props.selectedCurrency]);

    const isHistoryMode = Boolean(
        props.selectedCurrency?.sectionTitleWhenFromHistory != null &&
        props.selectedCurrency?.sectionEvents?.length > 0
    );

    const currentFeedCur = props.selectedCurrency?.sectionEvents?.[props.selectedCurrency?.index]?.cur;
    const historyChartTitle = comboHeaderTitle(eventInfo, sectionTitleWhenFromHistory);
    useEffect(() => {
        if (isHistoryMode && feedScrollRef.current != null) {
            feedScrollRef.current.scrollTop = 0;
        }
    }, [isHistoryMode, currentFeedCur]);

    useEffect(() => {
        if (isHistoryMode) setFeedSelectionByUser(false);
    }, [isHistoryMode, currentFeedCur]);

    const feedEventsWithIndex = useMemo(() => {
        if (!isHistoryMode || !props.selectedCurrency?.sectionEvents) return [];
        const sectionEvents = props.selectedCurrency.sectionEvents;
        const sectionTitle = props.selectedCurrency.sectionTitleWhenFromHistory;
        setSectionTitleWhenFromHistory(sectionTitle);
        const fullHistoryFlat = props.selectedCurrency.fullHistoryFlat || sectionEvents;
        const currentEv = sectionEvents[props.selectedCurrency.index];
        const currentCur = currentEv?.cur;
        if (!currentCur) return [];

        const normTf = (tf) => (tf || "").replace(/^_/, "").toUpperCase();
        const currentTf = normTf(currentEv?.tf);
        const tfToCheck = sectionEventTFWhenFromHistory || currentTf;
        const matchEvent = (se, e) => se.cur === e.cur && Number(se.ts) === Number(e.ts) && String(se.baseKey || "") === String(e.baseKey || "");

        const sectionIndicesForCur = sectionEvents
            .map((se, i) => ({ se, i }))
            .filter(({ se }) => se.cur === currentCur)
            .sort((a, b) => (b.se.ts || 0) - (a.se.ts || 0))
            .map(({ i }) => i);

        let list = fullHistoryFlat
            .filter((e) => e.cur === currentCur)
            .map((ev) => (ev));

        if (feedTab === "this_section" && sectionTitle) {
            list = list.filter((ev) => ev.sectionTitle === sectionTitle && normTf(ev.tf) === normTf(tfToCheck));
        }

        list.sort((a, b) => (b.ts || 0) - (a.ts || 0));

        // list = list.map(({ ev }, eidx) => {
        //     const sectionIdx = sectionEvents.findIndex((se) => matchEvent(se, ev));
        //     const idx = sectionIdx >= 0 ? sectionIdx : (sectionIndicesForCur[eidx] ?? sectionIndicesForCur[0] ?? 0);
        //     return { ev, idx, eidx };
        // });

        return [...list];
    }, [isHistoryMode, props.selectedCurrency?.sectionEvents, props.selectedCurrency?.index, props.selectedCurrency?.fullHistoryFlat, props.selectedCurrency?.sectionTitleWhenFromHistory, feedTab]);

    const handleMessage = useCallback((event) => {
        // Handle postMessage events (from iframe)
        const eventType = event.data?.type;
        const eventData = event.data?.data;

        switch (eventType) {
            case "chart_ready": {
                setBtnDisabled(false);
                break;
            }

            case "chart_failed": {
                const currentDate = new Date().getTime();
                const diff = currentDate - initCurrencyDate;
                if (diff < 4000) { break; }
                console.log("diff: " + diff);
                const curr = props.selectedCurrency?.sectionEvents[props.selectedCurrency?.index]?.cur;
                removeOpenedCurrency(curr);
                //setShowErrorModal(true);
                break;
            }

            case "trade_window": {
                if (userSessionStatus === "authenticated") {
                    if (tradingPanelOpen) {
                        setSelectedTradeCandle(eventData);
                        break;
                    }
                    setPriceBy(localStorage.getItem("tradePopup") || "PRICE");
                    setTradeData(eventData);
                    setTradeWindow(true);
                } else {
                    alert("Please sign in from Profile to use the trade window.");
                }
                break;
            }

            case "tpsl": {
                if (userSessionStatus === "authenticated") {
                    setPriceBy("TPSL");
                    const tradedCurrencies = JSON.parse(localStorage.getItem("tradedCurrencies")) || {};
                    const evtData = eventData;
                    if (tradedCurrencies && tradedCurrencies[evtData.currency]) {
                        evtData.tradeSide = tradedCurrencies[evtData.currency].tradeSide || "LONG";
                    }
                    setTradeData(evtData);
                    setTradeWindow(true);
                }
                break;
            }

            default: {}
        }
    }, [userSessionStatus, props.selectedCurrency, tradingPanelOpen]);

    const handleCustomEvent = useCallback((event) => {
        // Handle CustomEvent events (from commands.js and TradingViewChart.js)
        const eventType = event.type;
        const eventData = event.detail;

        switch (eventType) {
            case "chart_ready": {
                setBtnDisabled(false);
                break;
            }

            case "chart_failed": {
                const currentDate = new Date().getTime();
                const diff = currentDate - initCurrencyDate;
                if (diff < 4000) { break; }
                console.log("diff: " + diff);
                const curr = props.selectedCurrency?.sectionEvents[props.selectedCurrency?.index]?.cur;
                removeOpenedCurrency(curr);
                //setShowErrorModal(true);
                break;
            }

            case "trade_window": {
                if (userSessionStatus === "authenticated") {
                    if (tradingPanelOpen) {
                        setSelectedTradeCandle(eventData);
                        break;
                    }
                    setPriceBy(localStorage.getItem("tradePopup") || "PRICE");
                    setTradeData(eventData);
                    setTradeWindow(true);
                } else {
                    alert("Please sign in from Profile to use the trade window.");
                }
                break;
            }

            case "tpsl": {
                if (userSessionStatus === "authenticated") {
                    setPriceBy("TPSL");
                    const tradedCurrencies = JSON.parse(localStorage.getItem("tradedCurrencies")) || {};
                    const evtData = eventData;
                    if (tradedCurrencies && tradedCurrencies[evtData.currency]) {
                        evtData.tradeSide = tradedCurrencies[evtData.currency].tradeSide || "LONG";
                    }
                    setTradeData(evtData);
                    setTradeWindow(true);
                }
                break;
            }

            case "pick_price": {
                // Handle pick_price event if needed
                console.log("pick_price event:", eventData);
                break;
            }

            default: {}
        }
    }, [userSessionStatus, props.selectedCurrency, tradingPanelOpen]);

    useEffect(() => {
        if (props.show) {
            const sectionEvents = props.selectedCurrency.sectionEvents;
            if (!sectionEventTFWhenFromHistory) {
                setSectionEventTFWhenFromHistory(sectionEvents[props.selectedCurrency.index]?.tf);
            }
        } else {
            setSectionEventTFWhenFromHistory(null);
        }
    }, [props.show]);

    useEffect(() => {

        setInitCurrencyDate(new Date().getTime());

        if (props.show) {
            //setBtnDisabled(isMobile());
            // Listen to postMessage events (for backward compatibility)
            window.addEventListener('message', handleMessage);
            // Listen to CustomEvent events (from commands.js and TradingViewChart.js)
            window.addEventListener('chart_ready', handleCustomEvent);
            window.addEventListener('chart_failed', handleCustomEvent);
            window.addEventListener('trade_window', handleCustomEvent);
            window.addEventListener('tpsl', handleCustomEvent);
            window.addEventListener('pick_price', handleCustomEvent);
        }

        return () => {
            window.removeEventListener('message', handleMessage);
            window.removeEventListener('chart_ready', handleCustomEvent);
            window.removeEventListener('chart_failed', handleCustomEvent);
            window.removeEventListener('trade_window', handleCustomEvent);
            window.removeEventListener('tpsl', handleCustomEvent);
            window.removeEventListener('pick_price', handleCustomEvent);
        }
    }, [props.show, handleMessage, handleCustomEvent]);

    useEffect(() => {
        setEventInfo(getCurrency());
    }, [props.selectedCurrency, props.selectedCurrency?.index, getCurrency]);

    const currentCur = eventInfo?.cur ?? props.selectedCurrency?.sectionEvents?.[props.selectedCurrency?.index]?.cur;
    useEffect(() => {
        setSelectedTradeCandle(null);
        setSelectedCodexEvaluation(null);
    }, [currentCur]);

    useEffect(() => {
        setStarSelected(Boolean(currentCur && props.settings?.starred?.[currentCur]));
    }, [currentCur, props.settings?.starred]);

    useEffect(() => {
        setTradingPanelOpen(false);
    }, [props.show, currentCur]);

    const sendCommand = (cmd) => {
        if (userSessionStatus === "authenticated") {
            processCommand({ command: cmd }).catch(() => { /* Shared command notices show the result without logging trade payloads. */ });
            setTradeWindow(false);
        } else {
            alert("Please sign in from Profile to use the trade window.");
        }
    }

    const drawEvaluationLevels = (levels) => {
        window.dispatchEvent(new CustomEvent("chart_evaluation_draw", {
            detail: { symbol: `${currentCur}USDT`, levels }
        }));
    };

    const takeEvaluationTrade = (evaluationResult) => {
        if (evaluationResult?.symbol && evaluationResult.symbol !== `${currentCur}USDT`) return;
        if (!selectExecutableEvaluationPlan(evaluationResult)) return;
        const prefill = buildChartEvaluationTradePrefill(evaluationResult);
        setPriceBy("PRICE");
        setTradeData({
            currency: currentCur,
            candle: chartTimeframe || sectionEventTFWhenFromHistory || eventInfo?.tf,
            selectedCandle: selectedTradeCandle,
            ...prefill,
        });
        setShowChartEvaluation(false);
        setTradeWindow(true);
    };

    const selectedEvaluationPlan = selectExecutableEvaluationPlan({
        evaluation: selectedCodexEvaluation,
        requestedSide: selectedCodexEvaluation?._sharedRecord?.requestedSide,
    });
    const selectedEvaluationIsLight = ["LIGHT_V1", "REFERENCE_V1"].includes(selectedCodexEvaluation?.responseMode);
    const selectedEvaluationLevels = selectedEvaluationPlan ? {
        entry: selectedEvaluationPlan.entry,
        stopLoss: selectedEvaluationPlan.stopLoss,
        takeProfit1: selectedEvaluationPlan.takeProfit1,
        takeProfit2: selectedEvaluationPlan.takeProfit2,
    } : null;

    return (
        <>
            <Modal
                {...props}
                size="lg"
                aria-labelledby="contained-modal-title-vcenter"
                centered
                dialogClassName="custom-modal-width custom-modal-height">
                <Modal.Body
                    className={`bg-dark d-flex w-100 ${isMobile() ? "flex-column p-0" : "row"}`}
                    style={isMobile() ? { flexDirection: "column", position: "relative" } : {}}
                >
                    {(!isMobile()) && (
                    <div
                        ref={feedScrollRef}
                        className="history-feed-column"
                        style={{
                            flex: "0 0 32%",
                            maxWidth: "32%",
                            maxHeight: "100vh",
                            overflowY: "auto",
                            overflowX: "hidden",
                        }}
                    >
                        {tradingPanelOpen ? (
                            <TradingPanel
                                currency={currentCur}
                                timeframe={chartTimeframe || sectionEventTFWhenFromHistory || eventInfo?.tf}
                                selectedEvent={eventInfo}
                                selectedCandle={selectedTradeCandle}
                                open={tradingPanelOpen}
                                onClose={() => setTradingPanelOpen(false)}
                            />
                        ) : isHistoryMode ? (
                            <div
                                className="history-feed-panel"
                                style={{
                                    background: "linear-gradient(145deg, rgba(42,42,46,0.92) 0%, rgba(26,26,30,0.95) 100%)",
                                    color: "#fff",
                                    padding: "12px",
                                    borderRadius: "10px",
                                    minHeight: "min-content",
                                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
                                }}
                            >
                                <div className="mb-3">
                                    <div className="fw-bold" style={{ color: "#fff", fontSize: "1rem" }}>
                                        Feed – <AssetLabel symbol={getCurrency()?.cur} event={getCurrency()} />
                                    </div>
                                    <div className="d-flex gap-1 mt-2">
                                        <Button
                                            size="sm"
                                            variant={feedTab === "all" ? "primary" : "outline-secondary"}
                                            onClick={() => setFeedTab("all")}
                                        >
                                            All
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant={feedTab === "this_section" ? "primary" : "outline-secondary"}
                                            onClick={() => setFeedTab("this_section")}
                                        >
                                            This section
                                        </Button>
                                    </div>
                                </div>
                                <div key={`feed-list-${currentFeedCur ?? ""}`} className="d-flex flex-column gap-2">
                                    {feedEventsWithIndex.map((ev, index) => {
                                        const currentChartEv = props.selectedCurrency?.sectionEvents?.[props.selectedCurrency?.index];
                                        const isSelected = feedSelectionByUser
                                            && currentChartEv
                                            && ev.cur === currentChartEv.cur
                                            && Number(ev.ts) === Number(currentChartEv.ts)
                                            && String(ev.baseKey || "") === String(currentChartEv.baseKey || "")
                                            && ev.sectionTitle === currentChartEv.sectionTitle
                                            && ev.name === currentChartEv.name;

                                        const isBull = !!ev.bull;
                                        const isBear = !!ev.bear;
                                        const feedLabel = comboFeedLabel(ev);
                                        const stripeStyle =
                                            isBull && isBear
                                                ? { background: "linear-gradient(180deg, #1f8f5f 50%, #c73a49 50%)" }
                                                : isBull
                                                ? { background: "#1f8f5f" }
                                                : isBear
                                                ? { background: "#c73a49" }
                                                : { background: "rgba(255,255,255,0.35)" };
                                        let maVal = ev.ma != null ? ev.ma : (ev.maPct != null ? ev.maPct : null);
                                        let bbVal = ev.bb != null ? ev.bb : (ev.bbPct != null ? ev.bbPct : null);
                                        if ((maVal == null || bbVal == null) && typeof ev.desc === "string") {
                                            const maMatch = ev.desc.match(/MA\s*([\d.-]+)%?/i);
                                            const bbMatch = ev.desc.match(/BB\s*([\d.-]+)%?/i);
                                            if (maVal == null && maMatch) maVal = parseFloat(maMatch[1]);
                                            if (bbVal == null && bbMatch) bbVal = parseFloat(bbMatch[1]);
                                        }
                                        const maBbLine = [maVal != null && !Number.isNaN(maVal) && `MA ${Number(maVal).toFixed(1)}%`, bbVal != null && !Number.isNaN(bbVal) && `BB ${Number(bbVal).toFixed(1)}%`].filter(Boolean).join(", ") || null;
                                        const cardGradient = "linear-gradient(135deg, rgba(48,48,52,0.95) 0%, rgba(30,30,34,0.98) 100%)";
                                        return (
                                            <button
                                                key={`feed-${ev?.cur}-${ev?.ts}-${ev?.name}--${ev?.tf}`}
                                                type="button"
                                                className="text-start border-0 d-flex align-items-stretch overflow-hidden history-feed-card"
                                                style={{
                                                    background: cardGradient,
                                                    color: "rgba(180,180,185,0.95)",
                                                    padding: 0,
                                                    minHeight: "56px",
                                                    cursor: "pointer",
                                                    transition: "box-shadow 0.2s ease, transform 0.15s ease",
                                                    boxShadow: isSelected ? "0 0 0 1px rgba(234, 179, 8, 0.9)" : "0 1px 2px rgba(0,0,0,0.2)",
                                                }}
                                                onClick={() => {
                                                    setFeedSelectionByUser(true);
                                                    console.log("event: " + JSON.stringify(ev));
                                                    //props.setSelectedCurrency((prev) => (prev ? { ...prev, index: idx } : prev));
                                                    //props.setSelectedCurrency((prev) => (prev ? { ...prev, index: eidx } : prev));
                                                    props.setSelectedCurrency({ sectionEvents: feedEventsWithIndex, index: index, sectionTitleWhenFromHistory: sectionTitleWhenFromHistory });
                                                }}
                                            >
                                                <span
                                                    className="history-feed-card-stripe"
                                                    style={{
                                                        width: 5,
                                                        minWidth: 5,
                                                        flexShrink: 0,
                                                        ...stripeStyle,
                                                    }}
                                                    aria-hidden
                                                />
                                                <div className="flex-grow-1 p-2 text-start">
                                                    <div className="d-flex justify-content-between align-items-center mb-0 flex-wrap gap-1">
                                                        <div className="d-flex align-items-center gap-1 flex-wrap">
                                                            <span
                                                                className="history-feed-pill"
                                                                style={{
                                                                    background: "rgba(255,255,255,0.11)",
                                                                    color: "rgba(200,200,205,0.95)",
                                                                    padding: "3px 10px",
                                                                    borderRadius: "999px",
                                                                    fontSize: "0.95rem",
                                                                    fontWeight: 700,
                                                                }}
                                                            >
                                                                <AssetLabel symbol={ev.cur} event={ev} className="assetLabelCompact" />
                                                            </span>
                                                            <span
                                                                className="history-feed-pill"
                                                                style={{
                                                                    background: "rgba(255,255,255,0.11)",
                                                                    color: "rgba(200,200,205,0.95)",
                                                                    padding: "3px 10px",
                                                                    borderRadius: "999px",
                                                                    fontSize: "0.9rem",
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {(ev.tf || "").replace(/^_/, "") || "—"}
                                                            </span>
                                                            {isSelected ? (
                                                                <span
                                                                    style={{
                                                                        display: "inline-flex",
                                                                        alignItems: "center",
                                                                        gap: "4px",
                                                                        padding: "3px 10px",
                                                                        borderRadius: "999px",
                                                                        border: "1px solid rgba(255, 193, 7, 0.7)",
                                                                        background: "rgba(255, 193, 7, 0.12)",
                                                                        color: "#ffc107",
                                                                        fontSize: "0.85rem",
                                                                        fontWeight: 600,
                                                                    }}
                                                                >
                                                                    ★ Selected
                                                                </span>
                                                            ) : null}
                                                        </div>
                                                        <span style={{ fontSize: "0.9rem", fontWeight: 600, color: "rgba(170,170,175,0.9)" }}>{ageShort(ev.ts)}</span>
                                                    </div>
                                                    {feedLabel ? <div style={{ fontSize: "0.95rem", fontWeight: 600, color: "rgba(190,190,195,0.95)", marginTop: "2px" }}>{feedLabel}</div> : null}
                                                    {maBbLine ? <div style={{ fontSize: "0.9rem", fontWeight: 500, color: "rgba(160,160,165,0.9)", marginTop: "2px" }}>{maBbLine}</div> : null}
                                                    {ev.sectionTitle ? (
                                                        <div className="mt-1">
                                                            <span
                                                                className="history-feed-pill"
                                                                style={{
                                                                    background: "rgba(255,255,255,0.11)",
                                                                    color: "rgba(200,200,205,0.95)",
                                                                    padding: "3px 10px",
                                                                    borderRadius: "999px",
                                                                    fontSize: "0.85rem",
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {ev.sectionTitle}
                                                            </span>
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            <EventInfo
                                eventInfo={eventInfo}
                                insights={props.insights}
                                settings={props.settings}
                                setSettings={props.setSettings}
                            />
                        )}
                        {!tradingPanelOpen && (
                            <button
                                type="button"
                                className="tradingPanelToggle tradingPanelToggleDesktop"
                                onClick={() => setTradingPanelOpen(true)}
                            >
                                Trade
                            </button>
                        )}
                    </div>
                    )}
                    <div
                        className="history-chart-column"
                        style={{
                            flex: isMobile() ? "1 1 auto" : "1 1 68%",
                            minWidth: isMobile() ? "100%" : 0,
                            width: isMobile() ? "100%" : undefined,
                            minHeight: isMobile() ? "300px" : undefined,
                            position: isMobile() ? "relative" : undefined,
                            paddingTop: isMobile() ? 12 : undefined,
                            paddingLeft: isMobile() ? 10 : undefined,
                            paddingRight: isMobile() ? 10 : undefined,
                        }}
                    >
                        {!isMobile() && (
                            <div className="d-flex justify-content-between align-items-center mb-2 px-2">
                                <div style={{ color: "#fff", fontWeight: 700, fontSize: "1.1rem" }}>
                                    {isHistoryMode ? historyChartTitle : eventInfo?.name}
                                    {eventInfo?.cur ? <AssetLabel symbol={eventInfo.cur} event={eventInfo} iconOnly className="ms-2" /> : null}
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setShowChartEvaluation(true)}
                                        className="border-0 rounded-circle bg-info d-flex align-items-center justify-content-center shadow"
                                        style={{ width: 40, height: 40, flexShrink: 0 }}
                                        aria-label="Evaluate chart"
                                    >
                                        <FaMagic size={18} color="#06141b" />
                                    </button>
                                    {isHistoryMode && (
                                        <button
                                            type="button"
                                            onClick={() => setShowInfoModal(true)}
                                            className="border-0 rounded-circle bg-primary d-flex align-items-center justify-content-center shadow"
                                            style={{ width: 40, height: 40, flexShrink: 0 }}
                                            aria-label="Info"
                                        >
                                            <FaInfoCircle size={20} color="#fff" />
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (!currentCur) return;
                                            const next = !starSelected;
                                            const nextSettings = { ...props.settings, starred: { ...(props.settings?.starred || {}) } };
                                            if (next) {
                                                nextSettings.starred[currentCur] = eventInfo;
                                            } else {
                                                delete nextSettings.starred[currentCur];
                                            }
                                            props.setSettings(nextSettings);
                                            setStarSelected(next);
                                        }}
                                        className="border-0 rounded-circle d-flex align-items-center justify-content-center shadow"
                                        style={{
                                            width: 40,
                                            height: 40,
                                            flexShrink: 0,
                                            background: starSelected ? "linear-gradient(135deg, #e6b800 0%, #f0c800 100%)" : "rgba(255,255,255,0.12)",
                                            border: starSelected ? "2px solid rgba(255,215,0,0.8)" : "1px solid rgba(255,255,255,0.2)",
                                        }}
                                        aria-label={starSelected ? "Unfavourite" : "Favourite"}
                                    >
                                        {starSelected ? <FaStar size={20} color="#1a1a1a" /> : <FaRegStar size={20} color="#fff" />}
                                    </button>
                                </div>
                            </div>
                        )}
                        {isMobile() && (
                            <button
                                type="button"
                                onClick={() => setShowChartEvaluation(true)}
                                className="border-0 rounded-circle bg-info d-flex align-items-center justify-content-center shadow"
                                style={{ position: "fixed", bottom: 88, right: 144, width: 52, height: 52, zIndex: 1050 }}
                                aria-label="Evaluate chart"
                            >
                                <FaMagic size={22} color="#06141b" />
                            </button>
                        )}
                        {isMobile() && (
                            <div>
                                <div className="mb-2 px-1" style={{ color: "#fff", fontWeight: 700, fontSize: "1.1rem" }}>
                                    {isHistoryMode ? historyChartTitle : eventInfo?.name}
                                    {eventInfo?.cur ? <AssetLabel symbol={eventInfo.cur} event={eventInfo} iconOnly className="ms-2" /> : null}
                                </div>
                                <div className="mb-2 px-1" style={{ color: "#fff", fontWeight: 700, fontSize: "1.1rem" }}>
                                    {"CAP: " + (props.insights.currencyInfo[eventInfo?.cur]?.mcap)}
                                </div>
                            </div>
                        )}
                        <Graph
                            container="chart"
                            selectedEventData={props.selectedCurrency}
                            onEventChange={props.setSelectedCurrency}
                            onTradeEvent={handleMessage}
                            btcRegime={props.insights?.btcRegime}
                            compactMobile={isMobile()}
                            onTimeframeChange={setChartTimeframe}
                            onCodexEvaluationSelect={setSelectedCodexEvaluation}
                        />
                        {isMobile() && !tradingPanelOpen && (
                            <button
                                type="button"
                                className="tradingPanelToggle tradingPanelToggleMobile"
                                onClick={() => setTradingPanelOpen(true)}
                            >
                                Trade
                            </button>
                        )}
                        {isMobile() && (
                            <TradingPanel
                                currency={currentCur}
                                timeframe={chartTimeframe || sectionEventTFWhenFromHistory || eventInfo?.tf}
                                selectedEvent={eventInfo}
                                selectedCandle={selectedTradeCandle}
                                mobile={true}
                                open={tradingPanelOpen}
                                onClose={() => setTradingPanelOpen(false)}
                            />
                        )}
                        {isMobile() && (
                            <button
                                type="button"
                                onClick={() => {
                                    if (!currentCur) return;
                                    const next = !starSelected;
                                    const nextSettings = { ...props.settings, starred: { ...(props.settings?.starred || {}) } };
                                    if (next) {
                                        nextSettings.starred[currentCur] = eventInfo;
                                    } else {
                                        delete nextSettings.starred[currentCur];
                                    }
                                    props.setSettings(nextSettings);
                                    setStarSelected(next);
                                }}
                                className="border-0 rounded-circle d-flex align-items-center justify-content-center shadow"
                                style={{
                                    position: "fixed",
                                    bottom: 88,
                                    left: 24,
                                    width: 52,
                                    height: 52,
                                    zIndex: 1050,
                                    background: starSelected ? "linear-gradient(135deg, #e6b800 0%, #f0c800 100%)" : "rgba(255,255,255,0.12)",
                                    border: starSelected ? "2px solid rgba(255,215,0,0.8)" : "1px solid rgba(255,255,255,0.2)",
                                }}
                                aria-label={starSelected ? "Unfavourite" : "Favourite"}
                            >
                                {starSelected ? <FaStar size={26} color="#1a1a1a" /> : <FaRegStar size={26} color="#fff" />}
                            </button>
                        )}
                        {isMobile() && (
                            <div>
                                ({ isHistoryMode ? <button
                                    type="button"
                                    onClick={() => setShowHistoryModal(true)}
                                    className="border-0 rounded-circle bg-primary d-flex align-items-center justify-content-center shadow"
                                    style={{
                                        position: "fixed",
                                        bottom: 88,
                                        right: 84,
                                        width: 52,
                                        height: 52,
                                        zIndex: 1050,
                                    }}
                                    aria-label="History"
                                >
                                    <FaHistory size={26} color="#fff" />
                                </button> : (<div></div>)}
                                <button
                                    type="button"
                                    onClick={() => setShowInfoModal(true)}
                                    className="border-0 rounded-circle bg-primary d-flex align-items-center justify-content-center shadow"
                                    style={{
                                        position: "fixed",
                                        bottom: 88,
                                        right: 24,
                                        width: 52,
                                        height: 52,
                                        zIndex: 1050,
                                    }}
                                    aria-label="Info"
                                >
                                    <FaInfoCircle size={26} color="#fff" />
                                </button>
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer className="bg-dark d-flex justify-content-between">
                    <div className="d-flex flex-row overflow-auto" style={{ maxWidth: "100%" }}>
                        {getOpenedCurrencies().slice(0, isMobile() ? 2 : undefined).map((curr, index) => (
                            <a key={index} href={`?currency=${curr}`}>
                                <Button
                                        variant="secondary"
                                        className={"me-2"}
                                        style={{ padding: "6px 14px", fontSize: "1rem" , whiteSpace: "nowrap"}}>
                                    {curr}
                                </Button>
                            </a>
                        ))}
                        {isMobile() && getOpenedCurrencies().length > 2 && (
                            <Form.Select
                                value={selectedCurrency}
                                onChange={(e) => {
                                    const currency = e.target.value;
                                    if (currency) {
                                        setSelectedCurrency(currency);
                                        window.location.href = `?currency=${currency}`;
                                    }
                                }}
                                style={{
                                    width: "auto",
                                    minWidth: "120px",
                                    marginLeft: "8px",
                                    backgroundColor: "#343a40",
                                    color: "white",
                                    border: "1px solid #6c757d"
                                }}
                                size="sm">
                                <option value="">More...</option>
                                {getOpenedCurrencies().slice(2).map((curr, index) => (
                                    <option key={index + 2} value={curr}>{curr}</option>
                                ))}
                            </Form.Select>
                        )}
                    </div>
                    <Button
                        onClick={props.onHide}
                        //disabled={btnDisabled}
                        variant={btnDisabled ? "secondary" : "primary"}
                        style={{ fontWeight: 700, marginRight: 20 }}>Close</Button>
                </Modal.Footer>

                <TradePopup
                    priceBy={priceBy}
                    show={tradeWindow}
                    draw={eventInfo?.draw}
                    btnDisabled={btnDisabled}
                    setBtnDisabled={setBtnDisabled}
                    tradeData={tradeData}
                    chartTimeframe={chartTimeframe}
                    onCommand={(command) => sendCommand(command) }
                    onClose={ () => setTradeWindow(false) }/>
            </Modal>

            <ChartEvaluationPopup
                show={showChartEvaluation}
                onHide={() => setShowChartEvaluation(false)}
                symbol={`${currentCur || ""}USDT`}
                eventPrice={eventInfo?.price}
                originatingEvent={eventInfo}
                onDrawLevels={drawEvaluationLevels}
                onTakeTrade={takeEvaluationTrade}
            />

            <Modal
                show={Boolean(selectedCodexEvaluation)}
                onHide={() => setSelectedCodexEvaluation(null)}
                centered
                size={selectedEvaluationIsLight ? undefined : "xl"}
                className="bg-dark codex-analysis-modal"
                dialogClassName={selectedEvaluationIsLight ? "codex-light-analysis-dialog" : "custom-modal-width"}>
                <Modal.Header closeButton closeVariant="white" className="bg-dark text-white border-secondary">
                    <Modal.Title>{selectedEvaluationIsLight
                        ? selectedCodexEvaluation?.responseMode === "REFERENCE_V1" ? "AI reference comparison" : "AI event validation"
                        : selectedCodexEvaluation?._sharedRecord?.provider === "CUSTOM_AI"
                        ? "Custom AI chart evaluation" : "Codex market analysis"}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="bg-dark text-white overflow-auto" style={{ maxHeight: "82vh" }}>
                    <CodexEvaluationReport evaluation={selectedCodexEvaluation} />
                </Modal.Body>
                <Modal.Footer className="bg-dark border-secondary d-flex justify-content-end">
                    {selectedEvaluationLevels?.entry && <Button variant="outline-info" onClick={() => drawEvaluationLevels(selectedEvaluationLevels)}>Draw levels</Button>}
                    {selectedEvaluationLevels?.entry && selectedEvaluationLevels?.stopLoss && <Button variant="success" onClick={() => {
                        takeEvaluationTrade({
                            evaluation: {
                                ...selectedCodexEvaluation,
                                side: selectedEvaluationPlan.side,
                                timing: selectedEvaluationPlan.timing,
                                entry: selectedEvaluationLevels.entry,
                                stopLoss: selectedEvaluationLevels.stopLoss,
                                takeProfit1: selectedEvaluationLevels.takeProfit1,
                                takeProfit2: selectedEvaluationLevels.takeProfit2,
                            },
                            model: selectedCodexEvaluation?._sharedRecord?.model,
                            symbol: selectedCodexEvaluation?._sharedRecord?.symbol,
                            requestedSide: selectedCodexEvaluation?._sharedRecord?.requestedSide,
                        });
                        setSelectedCodexEvaluation(null);
                    }}>Take trade</Button>}
                    <Button variant="primary" onClick={() => setSelectedCodexEvaluation(null)}>Close</Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showInfoModal}
                onHide={() => setShowInfoModal(false)}
                centered
                size="lg"
                className="bg-dark info-modal-mobile"
                dialogClassName="custom-modal-width">
                <Modal.Header closeButton closeVariant="white" className="bg-dark text-white border-secondary">
                    <Modal.Title>{"Event info"}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="bg-dark text-white overflow-auto" style={{ maxHeight: "80vh" }}>
                    <EventInfo
                        eventInfo={eventInfo}
                        insights={props.insights}
                        settings={props.settings}
                        setSettings={props.setSettings}
                    />
                </Modal.Body>
                <Modal.Footer className="bg-dark border-secondary d-flex justify-content-end">
                    <Button variant="primary" onClick={() => setShowInfoModal(false)} style={{ fontWeight: 700 }}>Close</Button>
                </Modal.Footer>
            </Modal>

            {isMobile() && (
                <Modal
                    show={showHistoryModal}
                    onHide={() => setShowHistoryModal(false)}
                    centered
                    size="lg"
                    className="bg-dark info-modal-mobile"
                    dialogClassName="custom-modal-width">
                    <Modal.Header closeButton closeVariant="white" className="bg-dark text-white border-secondary">
                        <Modal.Title>{"Feed"}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="bg-dark text-white overflow-auto" style={{ maxHeight: "80vh" }}>
                        {(
                            <div
                                className="history-feed-panel"
                                style={{
                                    background: "linear-gradient(145deg, rgba(42,42,46,0.92) 0%, rgba(26,26,30,0.95) 100%)",
                                    color: "#fff",
                                    padding: "12px",
                                    borderRadius: "10px",
                                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
                                }}
                            >
                                <div className="mb-3">
                                    <div className="fw-bold" style={{ color: "#fff", fontSize: "1rem" }}>
                                        Feed – <AssetLabel symbol={getCurrency()?.cur} event={getCurrency()} />
                                    </div>
                                    <div className="d-flex gap-1 mt-2">
                                        <Button size="sm" variant={feedTab === "all" ? "primary" : "outline-secondary"} onClick={() => setFeedTab("all")}>All</Button>
                                        <Button size="sm" variant={feedTab === "this_section" ? "primary" : "outline-secondary"} onClick={() => setFeedTab("this_section")}>This section</Button>
                                    </div>
                                </div>
                                <div key={`feed-list-modal-${currentFeedCur ?? ""}`} className="d-flex flex-column gap-2">
                                    {feedEventsWithIndex.map((ev, index) => {
                                        const currentChartEv = props.selectedCurrency?.sectionEvents?.[props.selectedCurrency?.index];
                                        const isSelected = feedSelectionByUser && currentChartEv && ev.cur === currentChartEv.cur && Number(ev.ts) === Number(currentChartEv.ts) && String(ev.baseKey || "") === String(currentChartEv.baseKey || "") && ev.sectionTitle === currentChartEv.sectionTitle && ev.name === currentChartEv.name;
                                        const feedLabel = comboFeedLabel(ev);
                                        const isBull = !!ev.bull;
                                        const isBear = !!ev.bear;
                                        const stripeStyle = isBull && isBear ? { background: "linear-gradient(180deg, #1f8f5f 50%, #c73a49 50%)" } : isBull ? { background: "#1f8f5f" } : isBear ? { background: "#c73a49" } : { background: "rgba(255,255,255,0.35)" };
                                        let maVal = ev.ma != null ? ev.ma : (ev.maPct != null ? ev.maPct : null);
                                        let bbVal = ev.bb != null ? ev.bb : (ev.bbPct != null ? ev.bbPct : null);
                                        if ((maVal == null || bbVal == null) && typeof ev.desc === "string") {
                                            const maMatch = ev.desc.match(/MA\s*([\d.-]+)%?/i);
                                            const bbMatch = ev.desc.match(/BB\s*([\d.-]+)%?/i);
                                            if (maVal == null && maMatch) maVal = parseFloat(maMatch[1]);
                                            if (bbVal == null && bbMatch) bbVal = parseFloat(bbMatch[1]);
                                        }
                                        const maBbLine = [maVal != null && !Number.isNaN(maVal) && `MA ${Number(maVal).toFixed(1)}%`, bbVal != null && !Number.isNaN(bbVal) && `BB ${Number(bbVal).toFixed(1)}%`].filter(Boolean).join(", ") || null;
                                        return (
                                            <button
                                                key={`feed-modal-${ev?.cur}-${ev?.ts}-${ev?.name}--${ev?.tf}`}
                                                type="button"
                                                className="text-start border-0 d-flex align-items-stretch overflow-hidden history-feed-card"
                                                style={{ background: "linear-gradient(135deg, rgba(48,48,52,0.95) 0%, rgba(30,30,34,0.98) 100%)", color: "rgba(180,180,185,0.95)", padding: 0, minHeight: "56px", cursor: "pointer", boxShadow: isSelected ? "0 0 0 1px rgba(234, 179, 8, 0.9)" : "0 1px 2px rgba(0,0,0,0.2)" }}
                                                onClick={() => { setFeedSelectionByUser(true); props.setSelectedCurrency({ sectionEvents: feedEventsWithIndex, index: index, sectionTitleWhenFromHistory: sectionTitleWhenFromHistory }); setShowInfoModal(false); }}
                                            >
                                                <span className="history-feed-card-stripe" style={{ width: 5, minWidth: 5, flexShrink: 0, ...stripeStyle }} aria-hidden />
                                                <div className="flex-grow-1 p-2 text-start">
                                                    <div className="d-flex justify-content-between align-items-center mb-0 flex-wrap gap-1">
                                                        <div className="d-flex align-items-center gap-1 flex-wrap">
                                                            <span className="history-feed-pill" style={{ background: "rgba(255,255,255,0.11)", color: "rgba(200,200,205,0.95)", padding: "3px 10px", borderRadius: "999px", fontSize: "0.95rem", fontWeight: 700 }}><AssetLabel symbol={ev.cur} event={ev} className="assetLabelCompact" /></span>
                                                            <span className="history-feed-pill" style={{ background: "rgba(255,255,255,0.11)", color: "rgba(200,200,205,0.95)", padding: "3px 10px", borderRadius: "999px", fontSize: "0.9rem", fontWeight: 600 }}>{(ev.tf || "").replace(/^_/, "") || "—"}</span>
                                                            {isSelected && <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "3px 10px", borderRadius: "999px", border: "1px solid rgba(255, 193, 7, 0.7)", background: "rgba(255, 193, 7, 0.12)", color: "#ffc107", fontSize: "0.85rem", fontWeight: 600 }}>★ Selected</span>}
                                                        </div>
                                                        <span style={{ fontSize: "0.9rem", fontWeight: 600, color: "rgba(170,170,175,0.9)" }}>{ageShort(ev.ts)}</span>
                                                    </div>
                                                    {feedLabel ? <div style={{ fontSize: "0.95rem", fontWeight: 600, color: "rgba(190,190,195,0.95)", marginTop: "2px" }}>{feedLabel}</div> : null}
                                                    {maBbLine ? <div style={{ fontSize: "0.9rem", fontWeight: 500, color: "rgba(160,160,165,0.9)", marginTop: "2px" }}>{maBbLine}</div> : null}
                                                    {ev.sectionTitle ? <div className="mt-1"><span className="history-feed-pill" style={{ background: "rgba(255,255,255,0.11)", color: "rgba(200,200,205,0.95)", padding: "3px 10px", borderRadius: "999px", fontSize: "0.85rem", fontWeight: 600 }}>{ev.sectionTitle}</span></div> : null}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="bg-dark border-secondary d-flex justify-content-end">
                        <Button variant="primary" onClick={() => setShowHistoryModal(false)} style={{ fontWeight: 700 }}>Close</Button>
                    </Modal.Footer>
                </Modal>
            )}

            <Modal
                show={showErrorModal}
                onHide={() => {
                    setShowErrorModal(false);
                    props.onHide();
                }}
                centered
                size="sm">
                <Modal.Header className="bg-dark text-white">
                    <Modal.Title>Error</Modal.Title>
                </Modal.Header>
                <Modal.Body className="bg-dark text-white">
                    Could not load the chart. Maybe the currency name is wrong or no such FUTURE market found on Binance !
                </Modal.Body>
                <Modal.Footer className="bg-dark">
                    <Button
                        variant="primary"
                        onClick={() => {
                            setShowErrorModal(false);
                            //props.onHide();
                        }}>
                        OK
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default CurrencyPopup;
